import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomainService } from './domain-service';
import { environment } from 'environments/environment';

import 'rxjs/add/operator/toPromise';

/**
 * Servicio de Chat. Es el unico punto que carga, alimenta y retira el widget de Aunoa.
 *
 * <p>El widget lee la global AUNOA_USER_DATA una sola vez, cuando su iframe avisa de que ha
 * cargado, y a partir de ahi ignora cualquier cambio. Por eso el payload inicial lleva solo el
 * NIF y la poliza del producto se le envia despues con aunoachat.addContext(), que si publica
 * en el iframe en el momento de la llamada.</p>
 *
 * @author Inetum
 */
@Injectable({
    providedIn: 'root'
})
export class ChatService {

    /** Carga en curso o ya resuelta. Mientras exista, el widget no se vuelve a inyectar. */
    private widgetLoad: Promise<void> = null;

    constructor(private http: HttpClient,
                private domainService: DomainService,
                @Inject(DOCUMENT) private document: any) { }

    /**
     * Recupera el payload cifrado ({payload, iv}) con el NIF del usuario logado y el número
     * de póliza del producto indicado, para la identificación segura en el widget de chat.
     * Si no se indica producto, el backend devuelve el payload solo con el NIF.
     *
     * @param policyNumber número de póliza del producto seleccionado (opcional).
     */
    getChatPayload(policyNumber?: string): Promise<any> {
        let url = this.domainService.backendUrlWithContext + '/chat/payload';
        if (policyNumber) {
            url += '/' + encodeURIComponent(policyNumber);
        }
        return this.http.get(url).toPromise();
    }

    /**
     * Carga el widget de Aunoa, una sola vez por sesión. Las llamadas siguientes devuelven la
     * misma promesa sin inyectar nada, así que se puede invocar desde cualquier pantalla sin
     * preocuparse de duplicados.
     *
     * El orden importa: AUNOA_USER_DATA tiene que estar puesta ANTES de inyectar el script,
     * porque el widget la lee al cargar su iframe y ya no vuelve a mirarla.
     */
    ensureWidgetLoaded(): Promise<void> {
        if (null !== this.widgetLoad) {
            return this.widgetLoad;
        }

        this.widgetLoad = this.getChatPayload()
            .then(chatData => this.injectWidget(this.toUserData(chatData)))
            .catch(() => this.injectWidget({}));

        return this.widgetLoad;
    }

    /**
     * Envía al bot el contexto del producto elegido y abre el chat. Sustituye a la antigua
     * reasignación de AUNOA_USER_DATA, que no llegaba al bot porque el widget ya la había
     * leído: addContext() publica en el iframe en el momento de la llamada.
     *
     * @param policyNumber número de póliza del producto seleccionado.
     */
    openChatWithContext(policyNumber: string): Promise<void> {
        return this.ensureWidgetLoaded()
            .then(() => this.getChatPayload(policyNumber))
            .then(chatData => {
                this.addContext(this.toUserData(chatData));
                this.openChat();
            })
            .catch(() => {
                // Sin contexto se abre igual: el bot conserva el NIF de la carga inicial.
                this.openChat();
            });
    }

    /**
     * Retira el widget por completo. Necesario al cerrar sesión: ni el script de Aunoa ni sus
     * nodos se limpian solos, asi que el chat del usuario anterior seguia visible —y utilizable—
     * para el siguiente que entrase en la misma pestaña.
     */
    destroyWidget(): void {
        const doc = this.document;

        ['#aunoa-wraper', '#aunoa-overlay', 'script[src*="webchatbot.aunoa.ai"]']
            .forEach(selector => {
                const nodes = doc.querySelectorAll(selector);
                for (let i = 0; i < nodes.length; i++) {
                    const node = nodes[i];
                    if (node.parentNode) {
                        node.parentNode.removeChild(node);
                    }
                }
            });

        const win = window as any;
        delete win.aunoachat;
        delete win.AUNOA_LOADED;
        delete win.AUNOA_USER_DATA;

        this.widgetLoad = null;
    }

    /** Da forma al dato que espera el widget a partir de la respuesta del backend. */
    private toUserData(chatData: any): any {
        return {
            cipheredPayload: (chatData && chatData.payload) ? chatData.payload : '',
            iv: (chatData && chatData.iv) ? chatData.iv : ''
        };
    }

    private injectWidget(userData: any): void {
        (window as any).AUNOA_USER_DATA = userData;

        const script = this.document.createElement('script');
        script.src = `https://webchatbot.aunoa.ai/widget/widget.min.js?botid=${environment.aunoaKey}`;
        this.document.body.appendChild(script);
    }

    private addContext(userData: any): void {
        const aunoachat = (window as any).aunoachat;
        if (aunoachat && typeof aunoachat.addContext === 'function') {
            aunoachat.addContext(userData);
        }
    }

    private openChat(): void {
        const aunoachat = (window as any).aunoachat;
        if (aunoachat && typeof aunoachat.openChat === 'function') {
            aunoachat.openChat();
        }
    }
}
